import _ from "lodash";
import {
  PROJECT_GROUP_TYPE_DP_CAS,
  PROJECT_GROUP_TYPE_DP_TOK_ESSAY,
  PROJECT_GROUP_TYPE_DP_TOK_EXHIBITION,
  PROJECT_GROUP_TYPE_DP_EE,
  PROJECT_GROUP_TYPE_PERSONAL,
  PROJECT_GROUP_TYPE_COMMUNITY,
  PROJECT_GROUP_TYPE_SERVICE_AS_ACTON,
} from "Projects/Constants/stringConstants";
import { TODDLE_LIBRARY } from "Constants/stringConstants";

import {
  CAN_VIEW_AND_MANAGE_STUDENT_PROJECTS,
  ENABLE_GLOBAL_HEADER_ON_MULTIPLE_SCREENS,
} from "Constants/permissionConstants";

import {
  getFilteredProjectGroupTypes,
  getProjectGroupCategoryIdByType,
} from "Projects/modules/Utils";

import ACLStore from "lib/aclStore";
import { recordEvent } from "lib/eventTracker";

import {
  NESTED_ROUTE_MAPPINGS_FOR_GLOBAL_HEADER,
  NESTED_ROUTE_MAPPINGS_FOR_GLOBAL_HEADER_LOCAL,
} from "./nestedRoutesMap";

import ACLStorage from "lib/aclService";
import { getProjectGroupContexts } from "Platform/modules/PlatformUtils";
import { toddleIconTooltipOverlayStyle } from "../GlobalHeaderStyles";

const projectGroupOrder = {
  [PROJECT_GROUP_TYPE_DP_CAS]: 1,
  [PROJECT_GROUP_TYPE_DP_EE]: 2,
  [PROJECT_GROUP_TYPE_DP_TOK_ESSAY]: 3,
  [PROJECT_GROUP_TYPE_DP_TOK_EXHIBITION]: 4,
  [PROJECT_GROUP_TYPE_PERSONAL]: 5,
  [PROJECT_GROUP_TYPE_COMMUNITY]: 6,
  [PROJECT_GROUP_TYPE_SERVICE_AS_ACTON]: 7,
};

const getUniqueProjectGroupTypesAndCurriculum = ({ projectGroups }) => {
  return _.map(
    _.sortBy(
      _.map(
        _.uniqWith(
          projectGroups,
          (projectGroup1, projectGroup2) =>
            projectGroup1.type === projectGroup2.type &&
            projectGroup1.curriculumProgram?.id ===
              projectGroup2.curriculumProgram?.id
        ),
        projectGroup => ({
          type: projectGroup.type,
          order: projectGroupOrder[projectGroup.type],
          curriculumProgram: projectGroup?.curriculumProgram,
        })
      ),
      ["order"]
    ),
    projectGroup => {
      return {
        type: projectGroup.type,
        curriculumProgram: projectGroup?.curriculumProgram,
      };
    }
  );
};

export const getAvailableProjectGroupTypesForAllCurriculums = ({
  projectGroups,
  projectGroupCategoryIdByType,
}) => {
  const projectGroupTypes = getUniqueProjectGroupTypesAndCurriculum({
    projectGroups,
  });
  return _.filter(projectGroupTypes, ({ type, curriculumProgram }) => {
    const projectGroupCategoryId = projectGroupCategoryIdByType[type];
    const curriculumProgramId = _.get(curriculumProgram, "id");
    const isProjectGroupTypeAvailable = ACLStorage.getModuleSettingsValueByContext(
      CAN_VIEW_AND_MANAGE_STUDENT_PROJECTS,
      getProjectGroupContexts({
        projectGroupCategoryId,
        curriculumProgramId,
      })
    );
    return isProjectGroupTypeAvailable;
  });
};

const getUniqueProjectGroupTypes = ({ projectGroups }) => {
  return _.map(
    _.sortBy(
      _.map(
        _.uniqWith(
          projectGroups,
          (projectGroup1, projectGroup2) =>
            projectGroup1.type === projectGroup2.type
        ),
        projectGroup => ({
          type: projectGroup.type,
          order: projectGroupOrder[projectGroup.type],
        })
      ),
      ["order"]
    ),
    projectGroup => projectGroup.type
  );
};

//TODO: This logic is copied in JumpToFeature and Projects. Need to move it to a common place
export const getAvailableProjectGroupTypes = ({
  projectGroups,
  currentCurriculumProgramId,
  currentCurriculumProgramType,
  organizationId,
}) => {
  const projectGroupTypes = getUniqueProjectGroupTypes({ projectGroups });

  const projectGroupCategoryIdByType = getProjectGroupCategoryIdByType({
    curriculumProgramIds: currentCurriculumProgramId
      ? [currentCurriculumProgramId]
      : [],
    organizationId,
    curriculumType: currentCurriculumProgramType,
  });

  return getFilteredProjectGroupTypes({
    projectGroupTypes,
    projectGroupCategoryIdByType,
    currentCurriculumProgramId,
  });
};

export const getVisibleJumpToFeatures = ({
  userCoursesCount,
  userLearningCoursesCount,
  projectGroups,
  isFutureAcademicYearSelected,
  isCurriculumProgramFree,
  currentCurriculumProgramId,
  currentCurriculumProgramType,
  organizationId,
}) => {
  const JUMP_TO_FEATURES = [
    {
      key: "LEARNING_COURSE_FEED",
    },
    {
      key: "COURSE_FEED",
    },
    {
      key: "HOMEPAGE_FEATURES",
    },
    {
      key: "PROJECTS_FEED",
    },
  ];

  if (isCurriculumProgramFree) {
    return _.filter(JUMP_TO_FEATURES, feature => feature.key === "COURSE_FEED");
  }

  /**
   * This condition is true in a special case where teacher can access homepage of a curriculum program
   * without having any courses assigned to them. It is applicable for programs supporting project groups.
   */
  if (userCoursesCount === 0) {
    return _.filter(JUMP_TO_FEATURES, feature => {
      switch (feature.key) {
        case "COURSE_FEED":
          return false;
        case "PROJECTS_FEED":
          return _.size(projectGroups) > 0;
        case "LEARNING_COURSE_FEED":
          return userLearningCoursesCount != 0;
        default:
          return true;
      }
    });
  }

  const canShowProjectGroupsInFutureAY = ACLStore.can(
    "FeatureFlag:ShowProjectsInFutureAY"
  );

  const hideProjectGroupsForFutureAY =
    isFutureAcademicYearSelected && !canShowProjectGroupsInFutureAY;

  return _.filter(JUMP_TO_FEATURES, feature => {
    /**
     * Section for projects is only applicable for MYP and DP programs.
     * For other programs, we are removing is based on the fact that projectGroups is empty for them
     */
    if (feature.key === "PROJECTS_FEED") {
      return (
        !hideProjectGroupsForFutureAY &&
        !_.isEmpty(projectGroups) &&
        !_.isEmpty(
          getAvailableProjectGroupTypes({
            projectGroups,
            currentCurriculumProgramId,
            currentCurriculumProgramType,
            organizationId,
          })
        )
      ); //This check is required as it is possible that no project group is accessible to the user in MYP/DP
    }

    if (feature.key === "LEARNING_COURSE_FEED") {
      return userLearningCoursesCount != 0;
    }

    return true;
  });
};

export const HEX_CODES_FOR_SPECIAL_SYMBOLS = {
  SHIFT: "&#x21E7;",
  CMD: "&#x2318;",
  OPTION: "&#x2325;",
};

export const getFeatureFromKeyboardEvent = ({ e, shortcutsMapping }) => {
  let matchingShortcut;
  const isCombinationOfEvents = _.isArray(e);

  if (isCombinationOfEvents && _.size(e) > 1) {
    const codes = _.map(e, event => event.code);
    matchingShortcut = _.findKey(shortcutsMapping, shortcut => {
      return _.isEqual(codes, shortcut.combination);
    });
  } else {
    let event = e;
    if (isCombinationOfEvents) {
      event = _.first(e);
    }

    const { altKey, metaKey, shiftKey, code } = event;
    matchingShortcut = _.findKey(shortcutsMapping, shortcut => {
      let isMatchingResult = shortcut.code == code;

      if (altKey || shortcut.altKey) {
        isMatchingResult = isMatchingResult && altKey === shortcut.altKey;
      }

      if (metaKey || shortcut.metaKey) {
        isMatchingResult = isMatchingResult && metaKey === shortcut.metaKey;
      }

      if (shiftKey || shortcut.shiftKey) {
        isMatchingResult = isMatchingResult && shiftKey === shortcut.shiftKey;
      }

      return isMatchingResult;
    });
  }

  return matchingShortcut;
};

/**
 * This function returns the matching nested routes mappings for a given path
 * Worst case complexity of this function is O(n) where n is the number of pairs in params
 */
const _getPathMatchingInfo = ({ path, params = {}, nestedRoutesMappings }) => {
  // Exact match
  //This is true when path is static ex: 'courses'
  if (!_.isUndefined(nestedRoutesMappings[path])) {
    return [
      { matchingPath: path, nestedRoutesMappings: nestedRoutesMappings[path] },
    ];
  }

  const paramKeys = _.keys(params);

  // Params match
  // Use _.reduce to accumulate matching paths
  const paramsMatchResult = _.reduce(
    paramKeys,
    (result, key) => {
      /**
       * When path is dynamic, we are iterating over the params and params for which
       * value is equal to path, we are returning the nested routes mappings for that param
       */
      const paramKeyValue = params[key];
      if (paramKeyValue === path && nestedRoutesMappings[key]) {
        return [
          ...result,
          {
            matchingPath: key,
            nestedRoutesMappings: nestedRoutesMappings[key],
          },
        ];
      }

      return result;
    },
    []
  );
  if (!_.isEmpty(paramsMatchResult)) return paramsMatchResult;

  // Global match
  // Check for wildcard entry and add to result if it exists
  if (nestedRoutesMappings["*"]) {
    return [
      {
        matchingPath: "*",
        nestedRoutesMappings: nestedRoutesMappings["*"],
      },
    ];
  }

  return [];
};

/**
 *
 * --- VERY IMPORTANT FOR MAKING GLOBAL HEADER AVAILABLE ON DIFFERENT ROUTES ---
 *
 * This function checks whether given array of path segments is present in the nested routes mappings.
 * Worst case time complexity of this function is : O(n!), where n is the length of the path segments array.
 * This can occur when path is completely dynamic and we have to check all the possible combinations.
 */
const _getRoutePresenceStatus = _.memoize(
  ({ pathArray, params, nestedRoutesMappings, currentPathIndex = 0 }) => {
    //Base case 1: route is longer than depth of nested routes mappings
    if (
      currentPathIndex < _.size(pathArray) &&
      _.isEmpty(nestedRoutesMappings)
    ) {
      return "NOT_FOUND";
    }

    //Base case 2: route is shorter or equal to depth of nested routes mappings
    if (currentPathIndex === _.size(pathArray)) {
      return "FOUND";
    }

    const path = pathArray[currentPathIndex];

    const pathMatchingInfo = _getPathMatchingInfo({
      path,
      params,
      nestedRoutesMappings,
    });

    const pathMatchingInfoCount = _.size(pathMatchingInfo);

    for (let i = 0; i < pathMatchingInfoCount; i++) {
      const {
        matchingPath,
        nestedRoutesMappings: matchingNestedRoutesMapping,
      } = pathMatchingInfo[i];

      // Treat * as a wildcard that allows global header on all child routes
      if (matchingPath === "*") {
        return "FOUND";
      }

      const nestedRoutePresenseStatus = _getRoutePresenceStatus({
        pathArray,
        params: _.omit(params, [matchingPath]),
        nestedRoutesMappings: matchingNestedRoutesMapping,
        currentPathIndex: currentPathIndex + 1,
      });

      if (nestedRoutePresenseStatus === "FOUND") {
        return "FOUND";
      }
    }

    return "NOT_FOUND";
  },
  ({ pathArray, params, currentPathIndex }) =>
    JSON.stringify({
      pathArray,
      params,
      currentPathIndex,
    })
);

export const getGlobalHeaderVisibilityStatusBasedOnRoute = ({
  pathname,
  params,
}) => {
  let pathArray = _.split(pathname, "/");
  pathArray = _.dropRightWhile(pathArray, path => path === ""); // Handle trailing slash

  const isGlobalHeaderOnMultipleScreensEnabled = ACLStore.can(
    ENABLE_GLOBAL_HEADER_ON_MULTIPLE_SCREENS
  );

  const routeMatchStatus = _getRoutePresenceStatus({
    pathArray,
    params,
    nestedRoutesMappings: isGlobalHeaderOnMultipleScreensEnabled
      ? NESTED_ROUTE_MAPPINGS_FOR_GLOBAL_HEADER
      : NESTED_ROUTE_MAPPINGS_FOR_GLOBAL_HEADER_LOCAL,
  });

  return routeMatchStatus === "FOUND" ? "VISIBLE" : "NOT_VISIBLE";
};

export const getActiveValueForPlatformSwitcher = ({
  isAdminPage,
  isToddleLibrary,
  location,
  currentCurriculumProgramId,
}) => {
  const pathArray = _.split(_.get(location, "pathname", ""), "/");
  if (isAdminPage) {
    if (_.includes(pathArray, "admin-home")) return "ADMIN_HOME";
    else if (_.includes(pathArray, "settings")) return "ADMIN_SETTINGS";
  } else if (isToddleLibrary) {
    return TODDLE_LIBRARY;
  } else if (_.includes(pathArray, "my-home")) {
    return "MY_HOME";
  } else if (
    _.includes(pathArray, "accountSettings") ||
    _.includes(pathArray, "school")
  ) {
    return "ACCOUNT_SCHOOL_NAVIGATION";
  } else {
    return currentCurriculumProgramId;
  }
};

export const getIsGlobalSearchJumpToRevampEnabled = () =>
  ACLStore.can("FeatureFlag:EnableGlobalSearchJumpToRevamp");

export const getIsGlobalSearchJumpToV2Enabled = () => {
  return ACLStore.can("FeatureFlag:EnableGlobalSearchJumpToV2");
};

export const JUMP_TO_COLUMNS = {
  LEARNING_COURSE_FEED: "LEARNING_COURSE_FEED",
  COURSE_FEED: "COURSE_FEED",
  HOMEPAGE_FEATURES: "HOMEPAGE_FEATURES",
  PROJECTS_FEED: "PROJECTS_FEED",
};

export const trackJumpToCardClick = ({
  featureId,
  featureType,
  e,
  isMouseClick = false,
}) => {
  const shortcutKeyObj = !isMouseClick
    ? {
        shortcutKey: {
          key: e.code ?? "ENTER",
          isMeta: e.ctrlKey || e.metaKey || e.shiftKey || e.altKey,
        },
      }
    : {};

  return recordEvent({
    eventName: "JUMP_TO",
    eventData: {
      event_type: "NAVIGATION",
      triggerPoint: isMouseClick ? "MOUSE_CLICK" : "KEYBOARD",
      featureType,
      featureId,
      ...shortcutKeyObj,
    },
  });
};

export const HOME_TOOLTIP_PROPS = {
  overlayStyle: toddleIconTooltipOverlayStyle,
  align: { offset: [0, 4] },
  fixArrowPosition: true,
};

export const JUMP_TO_TOOLTIP_PROPS = {
  align: { offset: [0, 6] },
};
